import type { Comic } from '@tanko/shared'
import type { LibraryEntry } from './library-entries'

/**
 * 按下一本作品之後要做什麼。
 *
 * 書庫只有一份清單，因此沒有「我剛才點的是哪一頁」可以當作來源的表態，來源必須自己選：
 * 本機那一份可用且頁數與雲端相同就讀本機（省流量），否則讀雲端。兩邊頁數不同才問——
 * 那時兩份都真的讀得出內容，只是內容不一樣，該由使用者決定讀哪一份。
 */
export type OpenTarget =
  | { kind: 'read'; path: string; notice?: string }
  | { kind: 'choose'; comic: Comic; cloudPageCount: number }
  /** 這台裝置沒有檔案，雲端也還沒備份完成：擋在書庫講清楚，不讓人進閱讀器才失敗。 */
  | { kind: 'blocked' }

const localPath = (comic: Comic) => `/read/local/${encodeURIComponent(comic.id)}`
const cloudPath = (id: string) => `/read/cloud/${encodeURIComponent(id)}`

export async function openTarget(
  entry: LibraryEntry, cloudDetail: (id: string) => Promise<{ pageCount: number }>,
): Promise<OpenTarget> {
  if (!entry.readable) return { kind: 'blocked' }
  const local = entry.localReadable ? entry.local : undefined
  if (!local) return { kind: 'read', path: cloudPath(entry.comic.cloudComicId ?? entry.comic.id) }
  // 還沒備份完成的作品雲端沒有可讀的副本，沒有第二個來源可以比。
  if (!entry.backedUp || !local.cloudComicId) return { kind: 'read', path: localPath(local) }
  try {
    const detail = await cloudDetail(local.cloudComicId)
    if (detail.pageCount === local.pageCount) return { kind: 'read', path: localPath(local) }
    return { kind: 'choose', comic: local, cloudPageCount: detail.pageCount }
  } catch {
    // 比不了頁數就讀本機並講一句：本機那一份一定讀得出來，而擋住閱讀的代價更大。
    return { kind: 'read', path: localPath(local), notice: '目前無法比較雲端版本，改讀本地檔案' }
  }
}
